import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  mergeColumnOrder,
  moveColumn as moveInOrder,
} from "../utils/methods/columnOrder";
import type { DropSide } from "../utils/methods/columnOrder";

/**
 * User-defined column order, persisted in localStorage (browser only — not in
 * the saved dashboard filters). Each table uses its own storageKey, like
 * `useColumnWidths`.
 *
 * `defaultIds` is the table's current column list (may depend on the role);
 * the saved order is reconciled against it on every read, so renamed/removed
 * columns vanish and new ones appear next to their default neighbours.
 *
 * Usage:
 *   const { order, moveColumn, resetOrder } =
 *     useColumnOrder("dashboardColumnOrder.v1", defaultIds);
 */
export const useColumnOrder = (
  storageKey: string,
  defaultIds: readonly string[],
) => {
  const readStorage = useCallback((): string[] => {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.filter((v) => typeof v === "string")
        : [];
    } catch {
      return [];
    }
  }, [storageKey]);

  const [saved, setSaved] = useState<string[]>(readStorage);
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      try {
        if (saved.length)
          localStorage.setItem(storageKey, JSON.stringify(saved));
        else localStorage.removeItem(storageKey);
      } catch {
        /* ignore quota errors */
      }
    }, 150);
    return () => {
      if (writeTimer.current) clearTimeout(writeTimer.current);
    };
  }, [saved, storageKey]);

  const order = useMemo(
    () => mergeColumnOrder(defaultIds, saved),
    [defaultIds, saved],
  );

  const moveColumn = useCallback(
    (fromId: string, toId: string, side: DropSide) => {
      setSaved((prev) =>
        moveInOrder(mergeColumnOrder(defaultIds, prev), fromId, toId, side),
      );
    },
    [defaultIds],
  );

  const resetOrder = useCallback(() => setSaved([]), []);

  return { order, moveColumn, resetOrder, isCustomized: saved.length > 0 };
};
